// /////////////////////////////////////////////////////////////
// [ファイル] SfaProjectListPage.tsx
// [概要] 営業案件一覧（推進案件機能統合 リリース1）
//        タイプ（仕入あり／売上のみ）はシステム判定の 1 軸で絞り込む。
//        フェーズは仕入（仕入ありのみ）と売上の 2 列。抽出条件のフェーズは、販売前なら仕入フェーズ、販売開始後は売上フェーズで判定（バックエンド）。
//        仕入ありは決済日・契約予定、売上のみは計上月（確定日の月、無ければ予定年月）を表示し、
//        構成（仕入契約 n ／ 区画 n・受注・見込み・販売前）を要約する。
//        検索は旧「案件リスト」と同じ「抽出条件／並び替え」ダイアログ（保存済み条件は案件条件リストと共通）に「表示列」タブを足したもの。
//        列の定義と表示列の設定は components/sfa/sfaListColumns（案件名は先頭に固定、設定は利用者ごとにブラウザに残す）。
//        一覧の状態（条件・並び順・ページ・スクロール位置）は親 Layout（SfaProjectsLayout）が持つ。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, Chip, Fab, IconButton, Popover, TextField, Tooltip, Typography } from '@mui/material';
import * as Icons from '../../constants/icons';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../../constants/layout';
import { STANDARD_FAB_CTA_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import StandardDataTable from '../../components/StandardDataTable';
import SalesProjectSearchFilters from '../../components/SalesProjectSearchFilters';
import { useNotification } from '../../providers/NotificationProvider';
import { useSfaProjectsListContext } from '../../providers/SfaProjectsListProvider';
import { SFA_SORT_OPTIONS, buildListColumns, isDefaultListColumns } from '../../components/sfa/sfaListColumns';
import { SfaListFilters, DEFAULT_LIST_FILTERS, DEFAULT_LIST_SORT, SELECTABLE_PHASES, saveListScroll } from '../../hooks/useSfaStructure';

// 検索入力は重いテーブルと分離し、Enter または blur で確定したときだけ親へ反映する
const SearchInput = React.memo(function SearchInput({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [text, setText] = useState(value);
  React.useEffect(() => { setText(value); }, [value]);
  return (
    <TextField
      size="small" value={text} placeholder="案件名・物件所在地・取引先で検索"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { if (text !== value) onCommit(text); }}
      onKeyDown={(e) => { if (e.key === 'Enter') onCommit(text); }}
      slotProps={{ input: { startAdornment: <Icons.Search fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} /> } }}
      sx={{ minWidth: 300 }}
    />
  );
});

// タイプチップ ↔ 旧条件の案件種別 (仕入 / 売上)
const TYPE_OF_CHIP: Record<'' | 'purchase' | 'sales_only', string[]> = { '': [], purchase: ['purchase'], sales_only: ['sales'] };
const chipOfTypes = (types: string[] | undefined): '' | 'purchase' | 'sales_only' => {
  const t = types || [];
  const buy = t.includes('purchase'); const sale = t.includes('sales');
  return buy && !sale ? 'purchase' : sale && !buy ? 'sales_only' : '';
};

export default function SfaProjectListPage() {
  const navigate = useNavigate();
  const notify = useNotification();
  // 一覧の状態は親 Layout (SfaProjectsLayout) が持つ。詳細へ移って戻っても、ほかのメニューから戻っても条件が残る。
  // 案件条件リストから適用した条件もそこで受ける (保存したフェーズ条件はそのまま使う)
  const t = useSfaProjectsListContext();
  const { viewLevel, permissions, appliedFilterName, setAppliedFilterName, scrollPositionRef, scrollStorageKey } = t;

  // 通知 API は Provider の再描画で参照が変わるため、依存に入れると「通知→再描画→通知…」の無限ループになる。ref 経由で使う
  const notifyRef = useRef(notify); notifyRef.current = notify;
  useEffect(() => { if (t.error) notifyRef.current.error(t.error); }, [t.error]);

  // 一覧テーブルのスクロール位置を Layout (Context) 経由で復元する (旧画面の SalesProjectsManagement と同じ作りに、タブ内の保存を足したもの)
  //   - マウント時に保存値へ復元。詳細・新規登録から戻ったときは行を Layout が持っているので即時に戻る
  //   - HOME 経由・再読み込みでは行がまだ無いため、一覧の取得が終わってからもう一度戻す (pendingScrollRef)
  //   - スクロール時・アンマウント時・ページを離れる時に Layout の ref とタブ内の保存を更新
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollRef = useRef<number | null>(scrollPositionRef.current > 0 ? scrollPositionRef.current : null);
  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const target = scrollPositionRef.current;
    // 復元: まず即時、続いて次のフレームでも再適用 (レイアウト確定後の取りこぼし対策)
    el.scrollTop = target;
    const rafId = requestAnimationFrame(() => {
      if (el.scrollTop !== target) el.scrollTop = target;
    });
    // タブ内への保存はスクロールが止まってから (連続で書かない)
    let saveTimer: ReturnType<typeof setTimeout> | undefined;
    const saveNow = () => {
      clearTimeout(saveTimer);
      saveListScroll(scrollStorageKey, scrollPositionRef.current);
    };
    const handleScroll = () => {
      scrollPositionRef.current = el.scrollTop;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(saveNow, 200);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('pagehide', saveNow); // 再読み込み・タブを閉じる直前
    return () => {
      cancelAnimationFrame(rafId);
      // 退避: 0 で上書きしない (StrictMode の二重マウントや DOM detach 経由の 0 取得を防止)
      if (el.scrollTop > 0) scrollPositionRef.current = el.scrollTop;
      saveNow();
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('pagehide', saveNow);
    };
  }, [scrollPositionRef, scrollStorageKey]);
  // 行が無い状態で開いた (HOME 経由・再読み込み) ときは、一覧の取得が終わって行が描画されてから保存位置へ戻す。
  // 開いた直後は権限が 2 段階で決まり一覧を 2 回取ることがあるため、行が足りず届かなかったときは次の取得後にもう 1 回だけ試す
  const pendingTriesRef = useRef(0);
  useEffect(() => {
    const el = tableScrollRef.current;
    const target = pendingScrollRef.current;
    if (!el || target === null || t.loading || t.rows.length === 0) return;
    el.scrollTop = target;
    pendingTriesRef.current += 1;
    if (Math.abs(el.scrollTop - target) <= 1 || pendingTriesRef.current >= 2) pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      if (el.scrollTop !== target) el.scrollTop = target;
    });
  }, [t.loading, t.rows]);
  // ページ送り・表示件数の変更・抽出条件のリセットはそのページを見る目的の操作なので、一覧の先頭から表示する
  const scrollToTop = () => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
    scrollPositionRef.current = 0;
    pendingScrollRef.current = null;
    saveListScroll(scrollStorageKey, 0);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const closeSearch = () => setAnchorEl(null);

  // 列は利用者の表示列の設定（案件名が先頭、順番は設定どおり）に従って組み立てる。定義は components/sfa/sfaListColumns
  const columns = useMemo(() => buildListColumns(t.columns, navigate), [t.columns, navigate]);

  const rows = useMemo(() => t.rows.map((r) => ({ ...r, id: r.project_id })), [t.rows]);
  const currentChip = chipOfTypes(t.filters.project_type);
  const typeChip = (kind: '' | 'purchase' | 'sales_only', label: string) => (
    <Chip
      label={label} size="small" clickable
      color={currentChip === kind ? 'primary' : 'default'}
      variant={currentChip === kind ? 'filled' : 'outlined'}
      onClick={() => { t.updateFilters({ project_type: TYPE_OF_CHIP[kind], project_kind: '' }); }}
    />
  );
  // 条件・並び順・表示列のどれかが既定と違えば、ボタンを強調する
  const isDefault = JSON.stringify({ ...t.filters, search_word: '' }) === JSON.stringify({ ...DEFAULT_LIST_FILTERS, search_word: '' }) && isDefaultListColumns(t.columns);

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 2, width: '96%', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <Icons.ListAlt sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>営業案件管理リスト</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>{typeChip('', '全件')}{typeChip('purchase', '仕入あり')}{typeChip('sales_only', '売上のみ')}</Box>
        <Typography variant="caption" color="text.secondary">
          {currentChip === 'purchase' ? '仕入契約がある案件（土地仕入）' : currentChip === 'sales_only' ? '仕入契約の無い案件（建築受注・改修・仲介など）' : 'タイプは仕入契約の有無からシステムが判定'}
        </Typography>
        {appliedFilterName && <Chip size="small" color="primary" variant="outlined" icon={<Icons.Bookmarks />} label={`適用中: ${appliedFilterName}`} onDelete={() => { setAppliedFilterName(null); t.applyFilters(DEFAULT_LIST_FILTERS); t.setSort(DEFAULT_LIST_SORT); }} />}
        <Box sx={{ flex: 1 }} />
        <SearchInput value={t.filters.search_word || ''} onCommit={(v) => t.updateFilters({ search_word: v })} />
        <Button
          size="small" startIcon={<Icons.FilterAlt />} onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ ...STANDARD_TEXT_BUTTON_SX, ...(isDefault ? {} : { color: 'primary.main', fontWeight: 700 }) }}
        >
          抽出条件・並び替え
        </Button>
        <Fab variant="extended" size="medium" sx={STANDARD_FAB_CTA_SX} onClick={() => navigate('/sfa/projects/new')}>
          <Icons.Add sx={{ mr: 0.5 }} />案件の新規登録
        </Fab>
      </Box>

      {/* 抽出条件・並び替え（旧「案件リスト」と同じ部品。保存した条件は案件条件リストに並ぶ） */}
      <Popover
        open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={closeSearch}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', borderRadius: 2, overflow: 'hidden', border: '1px solid #edf2f7' } } }}
      >
        <Card sx={{ width: '650px', border: 'none', boxShadow: 'none' }}>
          <Box sx={{ p: 1, borderBottom: '1px solid #eee', bgcolor: '#f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', fontSize: '13px' }}>案件を検索・絞り込み</Typography>
            <Tooltip title="保存済み条件一覧（案件条件リスト）">
              <IconButton size="small" onClick={() => { closeSearch(); navigate('/salesprojectsfilters'); }}><Icons.Bookmarks sx={{ fontSize: 18, color: '#64748b' }} /></IconButton>
            </Tooltip>
          </Box>
          <SalesProjectSearchFilters
            filters={t.filters}
            sort={{ field: t.sortField, order: t.sortOrder }}
            viewLevel={viewLevel}
            phaseOptions={SELECTABLE_PHASES}
            sortOptions={SFA_SORT_OPTIONS}
            columns={t.columns}
            onColumnsChange={t.setColumns}
            onSortChange={(s) => { t.setSort(s); setAppliedFilterName(null); }}
            onSearch={(f) => { t.applyFilters(f as SfaListFilters); closeSearch(); setAppliedFilterName(null); }}
            onReset={scrollToTop}
          />
        </Card>
      </Popover>

      <Box sx={TABLE_FILL_SX}>
        <StandardDataTable
          rows={rows} columns={columns} loading={t.loading || permissions === null} loadingMessage="案件を取得中..."
          totalCount={t.total} page={t.page - 1} pageSize={t.pageSize}
          sortField={t.sortField} sortOrder={t.sortOrder}
          onPageChange={(p) => { t.setPage(p + 1); scrollToTop(); }}
          onPageSizeChange={(s) => { t.setPageSize(s); t.setPage(1); scrollToTop(); }}
          onSortChange={t.toggleSort}
          scrollContainerRef={tableScrollRef}
        />
      </Box>
    </Box>
  );
}
